// OrganizationContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Cookies from 'universal-cookie';

// Create a context for managing organization/scope state
const OrganizationContext = createContext();

// Custom hook to access the Organization context
export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
}

/**
 * OrganizationProvider - wraps children components and provides organization context
 * @param {ReactNode} children - The children that will use the context
 */
export const OrganizationProvider = ({ children }) => {
  const cookies = new Cookies();
  // Initialize state from localStorage
  const [orgId, setOrgId] = useState(() => {
    const lsOrg = localStorage.getItem('OrgId');
    if (lsOrg && lsOrg !== 'null' && lsOrg !== 'undefined') return lsOrg;
    const lsCurrentOrg = localStorage.getItem('currentOrg');
    if (lsCurrentOrg && lsCurrentOrg !== 'null' && lsCurrentOrg !== 'undefined') return lsCurrentOrg;
    const cookieOrg = cookies.get('organization_Id');
    return cookieOrg || '';
  });
  const [scopeId, setScopeId] = useState(localStorage.getItem('scopeId') || '0');
  const [currentNetwork, setCurrentNetwork] = useState(localStorage.getItem('currentNetwork'));
  const [currentDeviceGrpId, setCurrentDeviceGrpId] = useState(localStorage.getItem('currentDeviceGrpId'));
  // True once we know the user's actual scope + its id (from persisted storage, or after NavGroup
  // resolves the hierarchy tree and calls a setter). ScopeGuard waits for this before deciding to
  // redirect, so a user whose context is still hydrating is never bounced.
  const [scopeResolved, setScopeResolved] = useState(() => {
    const s = localStorage.getItem('scopeId');
    if (s == null) return false;
    if (s === '0') return true; // MSP is always resolvable
    if (s === '1') return !!localStorage.getItem('OrgId');
    if (s === '2') return !!localStorage.getItem('currentNetwork');
    if (s === '3') return !!localStorage.getItem('currentDeviceGrpId');
    return false;
  });
  const [selectedType, setSelectedType] = useState(() => {
    const stored = localStorage.getItem('selectedType');
    return stored 
      ? JSON.parse(stored) 
      : { id: "All Organizations", label: "All Organizations", type: "All Organizations", icon: "All Organizations" };
  });
  
  // Refresh trigger counter - incrementing this will signal components to refresh their data
  const [hierarchyRefreshTrigger, setHierarchyRefreshTrigger] = useState(0);

  // Function to update all organization-related context at once.
  // Stable identity so effects that depend on this setter (OrgLocationGuard) do not
  // re-fetch GET_ALL_ORG_LIST on every organization-context render.
  const updateOrganizationContext = useCallback((newOrgId, newScopeId, newCurrentNetwork, newSelectedType) => {
    setOrgId(newOrgId);
    setScopeId(newScopeId);
    setCurrentNetwork(newCurrentNetwork);
    setScopeResolved(true); // scope is now established
    if (newSelectedType) {
      setSelectedType(newSelectedType);
    }

    // Sync with localStorage for persistence
    if (newOrgId !== undefined) localStorage.setItem('OrgId', newOrgId || '');
    if (newScopeId !== undefined) localStorage.setItem('scopeId', newScopeId || '0');
    if (newCurrentNetwork !== undefined) localStorage.setItem('currentNetwork', newCurrentNetwork || '');
    if (newSelectedType) localStorage.setItem('selectedType', JSON.stringify(newSelectedType));
  }, []);

  // Helper function to get the correct organization ID based on scope.
  //
  // localStorage is the source of truth for the active scope: the post-login landing
  // (persistPostLoginScope) and NavGroup both persist scopeId/ids there directly. On a
  // direct-to-location login the React context state can lag behind (it initialises once at
  // app mount, before login writes the location scope), so relying on context alone made the
  // dashboard summaries call unified/v1/deviceSummary/.../0/0 (MSP) and get 52013
  // ("Invalid scope code") -> "Failed to load data". Prefer localStorage, fall back to context.
  const getOrganizationData = () => {
    const effScopeId = localStorage.getItem('scopeId') || scopeId || '0';
    let organizationId = '0';
    if (effScopeId === '0') {
      organizationId = '0'; // MSP level
    } else if (effScopeId === '1') {
      organizationId = localStorage.getItem('OrgId') || orgId || localStorage.getItem('currentOrg') || '';
    } else if (effScopeId === '2') {
      organizationId = localStorage.getItem('currentNetwork') || currentNetwork || '';
    } else if (effScopeId === '3') {
      organizationId = localStorage.getItem('currentDeviceGrpId') || currentDeviceGrpId || '';
    }

    return {
      organizationId,
      scopeId: effScopeId
    };
  };

  // Individual setter functions for granular updates
  const setOrganizationId = (newOrgId) => {
    setOrgId(newOrgId);
    localStorage.setItem('OrgId', newOrgId || '');
    setScopeResolved(true);
  };

  const setScopeLevel = (newScopeId) => {
    setScopeId(newScopeId);
    localStorage.setItem('scopeId', newScopeId );
    setScopeResolved(true);
  };

  const setCurrentNetworkId = (newCurrentNetwork) => {
    setCurrentNetwork(newCurrentNetwork);
    localStorage.setItem('currentNetwork', newCurrentNetwork || '');
    setScopeResolved(true);
  };

  // Option D: resolve organization scope from a deep-link URL orgName so a valid /organization/:orgName
  // link stays on the org page instead of bouncing to MSP. Returns the resolved orgId, or '' if it
  // cannot be recovered (caller then redirects up to MSP home).
  const recoverScopeFromRoute = useCallback((routeOrgName) => {
    if (!routeOrgName) return '';
    // 1) Persisted pair from a prior visit (covers refresh / deep-link to the current org).
    const savedName = localStorage.getItem('savedOrgName');
    const savedId = localStorage.getItem('OrgId');
    if (savedName === routeOrgName && savedId && savedId !== 'null' && savedId !== 'undefined') {
      setOrgId(savedId);
      setScopeId('1');
      localStorage.setItem('scopeId', '1');
      setScopeResolved(true);
      return savedId;
    }
    // 2) Best-effort lookup in the cached hierarchy tree, if the app has loaded it.
    //    (Cache shape varies; guarded so a miss just falls through to the redirect.)
    try {
      const tree = JSON.parse(localStorage.getItem('orgTree') || 'null');
      const orgs = Array.isArray(tree) ? tree : tree?.organizations || tree?.data || [];
      const match = orgs.find((o) => o?.name === routeOrgName || o?.orgName === routeOrgName);
      const matchedId = match?.id || match?.orgId;
      if (matchedId) {
        localStorage.setItem('OrgId', matchedId);
        localStorage.setItem('savedOrgName', routeOrgName);
        setOrgId(matchedId);
        setScopeId('1');
        localStorage.setItem('scopeId', '1');
        setScopeResolved(true);
        return matchedId;
      }
    } catch (_) {
      /* ignore parse errors, fall through to redirect */
    }
    // 3) Could not recover -> caller redirects up to MSP home.
    return '';
  }, []);

  const setSelectedTypeData = (newSelectedType) => {
    setSelectedType(newSelectedType);
    localStorage.setItem('selectedType', JSON.stringify(newSelectedType));
  };

  // Function to trigger a refresh of the hierarchy tree data
  // This can be called from any component (like GeneralSettings) after saving changes
  const refreshHierarchyTree = useCallback(() => {
    setHierarchyRefreshTrigger(prev => prev + 1);
  }, []);

  // Initialize scopeId to '0' if it's null (similar to generalSummary.jsx)
  useEffect(() => {
    if (scopeId === null) {
      setScopeLevel('0');
    }
    // Keep localStorage in sync if we hydrate from cookies
    if (!localStorage.getItem('OrgId') && orgId) {
      localStorage.setItem('OrgId', orgId);
    }
  }, [scopeId, orgId]);

  return (
    <OrganizationContext.Provider value={{
      // State values
      orgId,
      scopeId,
      currentNetwork,
      currentDeviceGrpId,
      scopeResolved,
      selectedType,
      hierarchyRefreshTrigger,

      // Helper functions
      getOrganizationData,
      updateOrganizationContext,
      recoverScopeFromRoute,
      refreshHierarchyTree,
      
      // Individual setters
      setOrganizationId,
      setScopeLevel,
      setCurrentNetworkId,
      setSelectedTypeData
    }}>
      {children}
    </OrganizationContext.Provider>
  );
};